import React from "react";
import logger from "../../utils/logger.js";

/**
 * ErrorBoundary component catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2),
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error details
    const errorDetails = {
      error: error.toString(),
      errorInfo: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      errorId: this.state.errorId,
    };

    logger.error("ErrorBoundary caught an error:", errorDetails);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // Report to error tracking service if available
    if (window.gtag) {
      window.gtag("event", "exception", {
        description: error.toString(),
        fatal: true,
        error_id: this.state.errorId,
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-container">
            <div className="error-boundary-content">
              <div className="error-icon">⚠️</div>
              <h1 className="error-title">Something went wrong</h1>
              <p className="error-description">
                We're sorry, but something unexpected happened. The error has
                been logged and our team will investigate.
              </p>

              {this.state.errorId && (
                <div className="error-id">
                  <strong>Error ID:</strong> {this.state.errorId}
                </div>
              )}

              <div className="error-actions">
                <button
                  onClick={this.handleRetry}
                  className="retry-button"
                  type="button"
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleReload}
                  className="reload-button"
                  type="button"
                >
                  Reload Page
                </button>
              </div>

              {process.env.NODE_ENV === "development" && this.state.error && (
                <details className="error-details">
                  <summary>Error Details (Development Only)</summary>
                  <div className="error-stack">
                    <h3>Error:</h3>
                    <pre>{this.state.error.toString()}</pre>

                    <h3>Component Stack:</h3>
                    <pre>{this.state.errorInfo?.componentStack}</pre>

                    <h3>Stack Trace:</h3>
                    <pre>{this.state.error.stack}</pre>
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component that wraps a component with ErrorBoundary
 */
export const withErrorBoundary = (Component, fallback) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

/**
 * Hook for handling errors in functional components
 */
export const useErrorHandler = () => {
  const handleError = React.useCallback((error, errorInfo = {}) => {
    const errorDetails = {
      error: error.toString(),
      errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    logger.error("useErrorHandler caught an error:", errorDetails);

    // Report to error tracking service if available
    if (window.gtag) {
      window.gtag("event", "exception", {
        description: error.toString(),
        fatal: false,
      });
    }
  }, []);

  return { handleError };
};

export default ErrorBoundary;
